// File: src/components/CategoryPills.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * CategoryPills — compact, UberEats-style
 *
 * Props:
 *  - categories?: { id: string; label: string; emoji?: string }[]
 *  - initialSelected?: string[]
 *  - selectedIds?: string[]                // controlled
 *  - onChange?: (ids: string[]) => void
 *  - multi?: boolean                       // default: true
 *  - size?: 'sm' | 'md'                    // default: 'sm' (compact)
 *  - showArrows?: boolean                  // default: false (keep minimal)
 */
export default function CategoryPills({
  categories,
  initialSelected = [],
  selectedIds,
  onChange,
  multi = true,
  size = "sm",
  showArrows = false,
}) {
  const DATA = useMemo(
    () =>
      categories?.length
        ? categories
        : [
            // ——— Your priority order ———
            { id: "friendship", label: "Friendship", emoji: "🫶" },
            { id: "wellness", label: "Wellness", emoji: "🧘" },
            { id: "nightlife", label: "Nightlife", emoji: "🎉" },
            { id: "art", label: "Art", emoji: "🎨" },
            { id: "women-only", label: "Women Only", emoji: "👯‍♀️" },
            { id: "men-only", label: "Men Only", emoji: "👬" },
            { id: "museum", label: "Museum", emoji: "🏛️" },

            // ——— The rest (order not critical) ———
            { id: "music", label: "Music", emoji: "🎵" },
            { id: "sports", label: "Sports", emoji: "🏀" },
            { id: "fitness", label: "Fitness", emoji: "🏃‍♀️" },
            { id: "networking", label: "Networking", emoji: "🤝" },
            { id: "food", label: "Food", emoji: "🍔" },
            { id: "wine", label: "Wine", emoji: "🍷" },
            { id: "outdoors", label: "Outdoors", emoji: "🌲" },
            { id: "movies", label: "Movies", emoji: "🎬" },
            { id: "lgbtq", label: "LGBTQ", emoji: "🌈" },
            { id: "college", label: "College", emoji: "🎓" },
            { id: "learning", label: "Learning", emoji: "📚" },
          ],
    [categories]
  );

  const [internal, setInternal] = useState(() => dedupe(initialSelected));
  const current = selectedIds ?? internal;

  useEffect(() => {
    if (!selectedIds && onChange) onChange(internal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internal]);

  const railRef = useRef(null);
  const shouldSnapRef = useRef(false); // ← controls one-time snap for first selection among first 4

  function toggle(id) {
    const prevLen = current.length;
    const clickedIndex = DATA.findIndex((d) => d.id === id);

    // Only allow a snap when transitioning 0 → 1 AND the clicked pill is in the first 4
    if (prevLen === 0 && clickedIndex > -1 && clickedIndex <= 3) {
      shouldSnapRef.current = true;
    }

    let next;
    if (multi) {
      next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
    } else {
      next = current.includes(id) ? [] : [id];
    }

    if (selectedIds) onChange?.(next);
    else {
      setInternal(next);
      onChange?.(next);
    }
  }

  function clearAll() {
    if (!current.length) return;
    if (selectedIds) onChange?.([]);
    else setInternal([]);
  }

  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const update = () => {
      setCanLeft(rail.scrollLeft > 4);
      setCanRight(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 4);
    };
    update();
    rail.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      rail.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // If the first-ever selection met the rules, snap left once
  useEffect(() => {
    if (!shouldSnapRef.current) return;
    const el = railRef.current;
    if (el) {
      try {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } catch {
        el.scrollLeft = 0;
      }
    }
    shouldSnapRef.current = false; // one-time
  }, [current.join("|")]);

  const S = styles(size);

  return (
    <div style={S.wrap} aria-label="Categories">
      {/* subtle gradient edges */}
      <div style={{ ...S.edge, ...S.edgeLeft, opacity: canLeft ? 1 : 0 }} />
      <div style={{ ...S.edge, ...S.edgeRight, opacity: canRight ? 1 : 0 }} />

      {showArrows && (
        <>
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scrollBy(railRef, -0.9)}
            style={{ ...S.chev, left: 2, opacity: canLeft ? 1 : 0.25 }}
            disabled={!canLeft}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scrollBy(railRef, +0.9)}
            style={{ ...S.chev, right: 2, opacity: canRight ? 1 : 0.25 }}
            disabled={!canRight}
          >
            ›
          </button>
        </>
      )}

      <div ref={railRef} style={S.rail}>
        {current.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            style={{ ...S.pill, ...S.pillClear }}
          >
            Clear
          </button>
        )}

        {DATA.map(({ id, label, emoji }) => {
          const active = current.includes(id);
          return (
            <button
              key={id}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(id)}
              style={{ ...S.pill, ...(active ? S.pillActive : {}) }}
              title={label}
            >
              <span aria-hidden="true" style={S.emoji}>
                {emoji || "•"}
              </span>
              <span style={S.txt}>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */
function dedupe(arr) {
  return [...new Set(arr || [])];
}
function scrollBy(ref, fraction) {
  const el = ref.current;
  if (!el) return;
  el.scrollBy({ left: el.clientWidth * fraction, behavior: "smooth" });
}

/* ---------------- styles ---------------- */
function styles(size) {
  // compact defaults
  const isSm = size === "sm";
  const H = isSm ? 30 : 34; // pill height
  const PADX = isSm ? 10 : 12; // pill horizontal padding
  const GAP = isSm ? 6 : 8; // spacing between pills
  const FS = isSm ? 12 : 13.5; // font size
  const EMOJI = isSm ? 14 : 16; // emoji size

  return {
    wrap: {
      position: "relative",
      width: "100%",
      background: "#fff",
      padding: "4px 0 6px",
    },
    rail: {
      display: "flex",
      alignItems: "center",
      gap: GAP,
      overflowX: "auto",
      overscrollBehaviorX: "contain",
      WebkitOverflowScrolling: "touch",
      padding: "0 4px",
      scrollSnapType: "x proximity",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    },
    edge: {
      pointerEvents: "none",
      position: "absolute",
      top: 0,
      bottom: 0,
      width: 20,
      transition: "opacity 160ms ease",
      background:
        "linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))",
    },
    edgeLeft: { left: 0 },
    edgeRight: { right: 0, transform: "scaleX(-1)" },
    chev: {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 2,
      width: 24,
      height: 24,
      borderRadius: 999,
      border: "1px solid rgba(0,0,0,0.08)",
      background: "#fff",
      color: "#111",
      fontWeight: 700,
      lineHeight: 1,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    pill: {
      scrollSnapAlign: "start",
      display: "inline-flex",
      alignItems: "center",
      gap: isSm ? 6 : 8,
      height: H,
      padding: `0 ${PADX}px`,
      borderRadius: 999,
      border: "1px solid #E6E6E6",
      background: "#FFFFFF",
      color: "#111111",
      fontSize: FS,
      fontWeight: 700,
      letterSpacing: 0.1,
      whiteSpace: "nowrap",
      cursor: "pointer",
      userSelect: "none",
      transition:
        "background 120ms ease, color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
      boxShadow: "0 0 0 rgba(0,0,0,0)",
      outline: "none",
      WebkitTapHighlightColor: "transparent",
    },
    pillActive: {
      background: "#eee",
      color: "#000",
      border: "1px solid #ddd",
    },
    pillClear: {
      background: "#F7F7F7",
      color: "#111",
      borderStyle: "dashed",
      borderColor: "#E0E0E0",
      fontWeight: 800,
    },
    emoji: {
      fontSize: EMOJI,
      lineHeight: 1,
      transform: "translateY(1px)",
    },
    txt: { lineHeight: 1 },
  };
}
